import type { Dispatch, SetStateAction } from 'react';
import { AnalyticsTranslationKeys } from 'intl/keys';
import type { IssueChartFilterKind } from 'shared/domain/analytics/issueChartFilters';
import type { ProcessType } from 'shared/domain/process/types/model';
import { getAvailableFields } from 'shared/domain/visibleField/availableFields';
import { createAvailableFieldsSet } from 'shared/domain/visibleField/createAvailableFieldsSet';
import type { VisibleFieldModel } from 'shared/domain/visibleField/visibleFieldModel';
import {
  ChartFilter,
  DateRangeObject,
  FilterTypes,
  FilterValue,
  FilterValues,
  IssueChartFilters,
  IssueChartFilterValue,
} from 'shared/types/analytics';
import type { HashMap, LabelledEntity } from 'shared/types/commonView';
import { fromDate14DaysEarlier, toDateToday } from '..';
import { createLabels, setDateRangeFilterValue, setMultiselectFilterValue } from '../filters';
import { sanitizeDateFilterValue } from '../model';
import { ISSUE_CHART_FILTER_DESCRIPTORS } from './issueChartFilterDescriptors';
import type { IssueChartFilterLabelSources, IssueChartFilterOptions } from './types';

type SetIssueChartFilters = Dispatch<SetStateAction<IssueChartFilters | undefined>>;

/** Filters of a chart that has nothing saved: the last 14 days, nothing chosen. */
export function createIssueChartDefaultFilterValues(kinds: readonly IssueChartFilterKind[]): FilterValues {
  return {
    dataScope: {
      labelId: AnalyticsTranslationKeys.tab,
      filters: [
        {
          labelId: AnalyticsTranslationKeys.dateRange,
          // Resolved in the project's timezone once the filters are built.
          value: { from: fromDate14DaysEarlier, to: toDateToday },
        },
        ...kinds.map((kind) => ({ labelId: ISSUE_CHART_FILTER_DESCRIPTORS[kind].labelId, value: [] })),
      ],
    },
  };
}

function createDateRangeFilter(
  dateRangeValue: FilterValue,
  timezone: string,
  setFilters: SetIssueChartFilters
): ChartFilter<IssueChartFilterValue> {
  return {
    labelId: AnalyticsTranslationKeys.dateRange,
    type: FilterTypes.daterange,
    value: {
      from: sanitizeDateFilterValue(dateRangeValue.value.from, timezone),
      to: sanitizeDateFilterValue(dateRangeValue.value.to, timezone),
    },
    setter: (value: DateRangeObject): void => {
      setFilters((previousFilters) => previousFilters && setDateRangeFilterValue(previousFilters, value));
    },
  };
}

interface MultiselectFilterInput {
  kind: IssueChartFilterKind;
  /** Position in the data scope: the date range is first. */
  position: number;
  selectedIds: string[];
  available: LabelledEntity[];
  /** Names of the values, when wider than `available`. */
  labelSource?: LabelledEntity[];
  setFilters: SetIssueChartFilters;
}

function createMultiselectFilter({
  kind,
  position,
  selectedIds,
  available,
  labelSource = available,
  setFilters,
}: MultiselectFilterInput): ChartFilter<IssueChartFilterValue> {
  const { labelId, fieldRelation } = ISSUE_CHART_FILTER_DESCRIPTORS[kind];
  return {
    labelId,
    type: FilterTypes.multiselect,
    available,
    labels: createLabels(labelSource),
    value: selectedIds,
    setter: (value: string[]): void => {
      setFilters((previousFilters) => previousFilters && setMultiselectFilterValue(previousFilters, position, value));
    },
    fieldRelation,
  };
}

interface IssueChartFiltersInput {
  kinds: readonly IssueChartFilterKind[];
  /** Chosen values: the date range, then one per kind, in the order of `kinds`. */
  values: FilterValues;
  options: IssueChartFilterOptions;
  labelSources?: IssueChartFilterLabelSources;
  timezone: string;
  setFilters: SetIssueChartFilters;
}

/** The filters the chart shows, with the chosen values and what each filter offers. */
export function createIssueChartFilters({
  kinds,
  values,
  options,
  labelSources = {},
  timezone,
  setFilters,
}: IssueChartFiltersInput): IssueChartFilters {
  const [dateRangeValue, ...kindValues] = values.dataScope.filters;
  return {
    dataScope: {
      labelId: AnalyticsTranslationKeys.tab,
      filters: [
        createDateRangeFilter(dateRangeValue, timezone, setFilters),
        ...kinds.map((kind, kindIndex) =>
          createMultiselectFilter({
            kind,
            position: kindIndex + 1,
            selectedIds: kindValues[kindIndex]?.value ?? [],
            available: options[kind],
            labelSource: labelSources[kind],
            setFilters,
          })
        ),
      ],
    },
  };
}

/** Issue fields shown in any of the processes: filters of the other fields are cleared. */
export function createIssueChartAvailableFields(
  visibleFieldsByProcess: HashMap<VisibleFieldModel[]>,
  processes: ProcessType[]
): Set<string> {
  const fieldsAlwaysShown = getAvailableFields().filter((field) => !field.canBeDisabled);
  return createAvailableFieldsSet(visibleFieldsByProcess, fieldsAlwaysShown, processes);
}
